import { useRef } from 'react';
import styled, { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { Placeholder, Text } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { focusRing } from '@/app/components/search/styles/mixins';
import { CheckMark, useStepKeys } from '@/app/components/hotel-detail/evo/EvoBreadcrumbs';
import { useEvoStepperV2Enabled } from '@/app/components/hotel-detail/evo/EvoStepper';
import { useTranslation } from '@/app/components/translations/Translation';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useUrlSearchParams } from '@/app/components/hotel-detail/UrlSearchParamsProvider';
import OpenXModal from '@/app/components/search/components/Modal/OpenXModal';
import ModalHeader from '@/app/components/search/components/Modal/LayoutElements/ModalHeader';
import { PriceSummary } from '@/app/components/hotel-detail/PriceSummary';
import { HotelSummarySection } from '@/app/components/hotel-detail/evo/trip-overview/HotelSummarySection';
import { MobileTransportSummary } from '@/app/components/hotel-detail/evo/trip-overview/MobileTransportSummary';
import { mergeBrandedFareIntoMealPlan } from '@/app/components/hotel-detail/lib/brandedFarePricing';
import { TripOverviewPriceToggle } from '@/app/components/hotel-detail/evo/trip-overview/TripOverviewPriceToggle';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space200};
  padding: ${({ theme }) => theme.kallisto.space.space200};
`;

const Divider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderDisabled};
`;

const Steps = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.kallisto.space.space200};
`;

const StepRow = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.kallisto.space.space150};

  ${(p) =>
    p.$clickable &&
    css`
      appearance: none;
      margin: 0;
      padding: 0;
      background: none;
      border: none;
      border-radius: ${p.theme.kallisto.space.space50};
      cursor: pointer;
      font: inherit;
      text-align: left;

      &:focus-visible {
        ${focusRing}
      }
    `}
`;

const StepCircle = styled.div<{ $active: boolean; $completed: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radiusFull};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $completed, theme }) =>
    $completed ? theme.kallisto.color.background.static.backgroundNeutralContrast : 'transparent'};
  border: ${({ $active, $completed, theme }) =>
    $active
      ? `2px solid ${theme.kallisto.color.background.static.backgroundNeutralContrast}`
      : $completed
        ? 'none'
        : `1px solid ${theme.kallisto.color.border.static.borderNeutral}`};
`;

const StepLabel = styled(Text)<{ $link?: boolean }>`
  ${(p) =>
    p.$link &&
    css`
      text-decoration: underline;
    `}
`;

function TripSteps({
  activeStep,
  onStepClick,
}: {
  activeStep: number;
  /** Called when a completed step is clicked. Completed steps become links only when provided. */
  onStepClick?: (stepNumber: number) => void;
}) {
  const { t } = useTranslation();
  const stepKeys = useStepKeys();

  return (
    <Steps>
      {stepKeys.map((stepKey, index) => {
        const stepNumber = index + 1;
        const isActive = stepNumber === activeStep;
        const isCompleted = stepNumber < activeStep;
        const isClickable = isCompleted && !!onStepClick;
        const content = (
          <>
            <StepCircle $active={isActive} $completed={isCompleted}>
              {isCompleted ? (
                <CheckMark />
              ) : (
                <Text variant="BodySmall" weight="bold" color="textPrimary">
                  {stepNumber}
                </Text>
              )}
            </StepCircle>
            <StepLabel variant="Body" weight={isActive ? 'bold' : 'regular'} color="textPrimary" $link={isClickable}>
              {t(stepKey)}
            </StepLabel>
          </>
        );
        return isClickable ? (
          <StepRow key={stepKey} as="button" type="button" $clickable={true} onClick={() => onStepClick(stepNumber)}>
            {content}
          </StepRow>
        ) : (
          <StepRow key={stepKey}>{content}</StepRow>
        );
      })}
    </Steps>
  );
}

/**
 * Body of the "Your trip" modal: a mix of the EVO breadcrumbs (vertical step
 * list) and the EVO trip overview content (hotel summary, flight summary and
 * price). Opened from EvoTripSummaryBar on mobile. With evo_stepper_v2_enabled
 * the steps live in the StepperV2 bar and its sheet instead, so the modal drops
 * its step list (MPH-1452).
 */
function EvoTripSummaryContent({
  activeStep,
  onStepClick,
}: {
  activeStep: number;
  onStepClick?: (stepNumber: number) => void;
}) {
  const { selectedMealPlan, selectedBrandedFare } = useSelectedMealPlan();
  const { accommodationOnly } = useUrlSearchParams();
  // This overlay covers the page, so it carries its own copy of the toggle over the final price —
  // wherever the toggler test would show one at all.
  const { canShowTotalPrice: showPriceToggle } = usePriceDisplay();
  const evoStepperV2Enabled = useEvoStepperV2Enabled();

  const displayMealPlan = selectedMealPlan
    ? mergeBrandedFareIntoMealPlan(selectedMealPlan, selectedBrandedFare)
    : undefined;

  return (
    <Content>
      {!evoStepperV2Enabled && (
        <>
          <TripSteps activeStep={activeStep} onStepClick={onStepClick} />
          <Divider />
        </>
      )}
      <HotelSummarySection showModifyLink={false} showInfoIcons={false} headerVariant="Title4" />
      <Divider />
      {/* Same gate as EvoTripOverview and EvoTripOverviewMobile: with no transport on the offers
          response this section paints an empty "Flight from …" header over empty luggage rows. */}
      {!accommodationOnly && (
        <>
          <MobileTransportSummary />
          <Divider />
        </>
      )}
      {showPriceToggle && <TripOverviewPriceToggle />}
      {displayMealPlan ? (
        <PriceSummary mealPlan={displayMealPlan} showFromLabel={accommodationOnly} />
      ) : (
        <Placeholder height="24px" width="150px" />
      )}
    </Content>
  );
}

export function EvoTripSummaryModal({
  isOpen,
  setOpen,
  activeStep = 1,
}: {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  activeStep?: number;
}) {
  const { staticData, localization } = usePageWrapperData();
  const { handleEvoGoToHotelDetails } = useCommonClient();
  const { t } = useTranslation();
  const tripSummaryTitle = t('vacation.oss.evo_trip_summary.title') || 'Your trip details';
  // The open modal holds its own history entry (?modal=..., pushed by useBrowserBackButton), so
  // navigating right away would leak that entry/param into the hotel-detail URL and reopen the
  // modal there. Instead the click only closes the modal and flags the navigation as pending;
  // it runs in onFinallyHistoryBack, after the modal's own history.back() has been performed.
  const goToHotelDetailsPending = useRef(false);

  const onStepClick = (stepNumber: number) => {
    if (stepNumber === 1) {
      goToHotelDetailsPending.current = true;
      setOpen(false);
    }
  };

  const onFinallyHistoryBack = () => {
    if (!goToHotelDetailsPending.current) return;
    goToHotelDetailsPending.current = false;
    // Defer to the next tick so history.back() has fully settled window.location before
    // handleEvoGoToHotelDetails reads it (same as InlineDatePicker's onFinallyHistoryBack).
    setTimeout(() => handleEvoGoToHotelDetails(), 0);
  };

  return (
    <OpenXModal
      isOpen={isOpen}
      isMobile={true}
      brand={staticData.domainValues?.brand as Brand}
      onClose={() => setOpen(false)}
      onCloseByBrowserHistory={() => setOpen(false)}
      onOpenByBrowserHistory={() => setOpen(true)}
      onFinallyHistoryBack={onFinallyHistoryBack}
      fullScreen={false}
      className="your-trip-modal"
      id="your-trip-modal"
      aria-label={tripSummaryTitle}
      bodyBackgroundColor="gray0"
      bodyPadding={0}
      bodyPaddingS={0}
      bodyPaddingXS={0}
      scrollShadows={localization?.configValuesApp?.evolutionEnabled}
      header={{
        sticky: true,
        element: (
          <ModalHeader
            handleClose={() => setOpen(false)}
            closeLabel={t('vacation.oss.evo_trip_summary.close') || 'Close trip details'}
          >
            {tripSummaryTitle}
          </ModalHeader>
        ),
      }}
    >
      <EvoTripSummaryContent activeStep={activeStep} onStepClick={onStepClick} />
    </OpenXModal>
  );
}
